import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, setTokens } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This component handles the OAuth callback from Emergent auth provider.
// The browser arrives here at #session_id=... after Google sign-in.
export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const { t, lang } = useLanguage();
  const [error, setError] = useState(null);
  // useRef (not useState) to make the guard synchronous under StrictMode.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Missing session_id");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", {
          session_id: sessionId,
          terms_accepted_disclaimer: true,
          terms_accepted_no_refund: true,
        });
        // Persist tokens for the Bearer fallback (Safari third-party cookies).
        setTokens(data.access_token, data.refresh_token);
        // Clear the hash BEFORE updating auth state so AuthContext doesn't
        // early-return on its session_id guard.
        window.history.replaceState(null, "", window.location.pathname);
        // Set user directly from the server response — no need to refetch /me.
        setUser(data.user);
        const dest = data.user?.role === "admin" ? "/admin" : "/dashboard";
        navigate(dest, { replace: true, state: { user: data.user } });
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen grid place-items-center px-6" data-testid="auth-callback-page">
      <Card className="w-full max-w-md p-8 border border-slate-200 shadow-sm text-center">
        {!error ? (
          <>
            <Loader2 className="w-10 h-10 text-slate-700 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 text-sm">
              {lang === "ar" ? "جارٍ إكمال تسجيل الدخول…" : "Completing sign-in…"}
            </p>
          </>
        ) : (
          <>
            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" data-testid="auth-callback-error-icon" />
            <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">
              {lang === "ar" ? "تعذّر تسجيل الدخول" : "Sign-in failed"}
            </h1>
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/login")} variant="outline" data-testid="auth-callback-back-btn">
              {t("cta_login")}
            </Button>
          </>
        )}
      </Card>
    </main>
  );
};

export default AuthCallback;
